import { useEffect, useState, type FormEvent } from "react";
import { useLocale } from "../../shared/i18n/LocaleContext";
import type { TranslationKey } from "../../shared/i18n/translations";
import {
  getOperations,
  operationCommand,
  type OperationsSnapshot,
  type Row,
} from "./operationsApi";
import { AppModal } from "../../shared/components/AppModal";
import { ClientRecordListControls } from "../../shared/components/ClientRecordListControls";
import { useClientRecordList } from "../../shared/components/useClientRecordList";
import { SortableHeader } from "../../shared/components/SortableHeader";
export type OperationsSection =
  | "shopFloor"
  | "operators"
  | "productionConfiguration"
  | "inventoryCounts"
  | "warehouseTransfers"
  | "salesPickings"
  | "salesReturns"
  | "purchaseMatching";
const k = (v: string) => v as TranslationKey;
export function OperationsPage({ section }: { section: OperationsSection }) {
  const { t, formatNumber } = useLocale();
  const [data, setData] = useState<OperationsSnapshot | null>(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const load = () =>
    getOperations(section)
      .then(setData)
      .catch((e) =>
        setError(t(k(e instanceof Error ? e.message : "operations.loadError"))),
      );
  useEffect(() => {
    void load();
  }, [section]);
  const records = collection(data ?? ({} as OperationsSnapshot), section);
  const list = useClientRecordList(records, `operations:${section}`);
  if (error)
    return (
      <main className="page-content">
        <p className="error-banner" role="alert">
          {error}
        </p>
      </main>
    );
  if (!data)
    return (
      <main className="page-content">
        <p>{t("common.loading")}</p>
      </main>
    );
  const command = async (path: string, body?: unknown) => {
    try {
      await operationCommand(path, "POST", body);
      await load();
    } catch (e) {
      setError(t(k(e instanceof Error ? e.message : "operations.saveError")));
    }
  };
  return (
    <main className="page-content">
      <header className="welcome administration-heading">
        <div>
          <span className="eyebrow">{t(k("operations.eyebrow"))}</span>
          <h1>{t(k(`operations.${section}`))}</h1>
          <p>{t(k(`operations.${section}Description`))}</p>
        </div>
      </header>
      {editing && (
        <SectionForm
          section={section}
          data={data}
          done={() => {
            setEditing(false);
            void load();
          }}
        />
      )}
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t(k(`operations.${section}`))}</h2>
          <button className="primary-button" onClick={() => setEditing(true)}>
            {section === "productionConfiguration"
              ? t("common.edit")
              : `+ ${t("administration.add")}`}
          </button>
        </div>
        <ClientRecordListControls list={list} />
        <div className="administration-table-wrap">
          <table className="data-table administration-table">
            <thead>
              <tr>
                <SortableHeader
                  label={t("aplPanel.code")}
                  active={list.sort === "code"}
                  direction={list.direction}
                  onClick={() => list.changeSort("code")}
                />
                <SortableHeader
                  label={t("aplPanel.name")}
                  active={list.sort === "name"}
                  direction={list.direction}
                  onClick={() => list.changeSort("name")}
                />
                <SortableHeader
                  label={t("planning.status")}
                  active={list.sort === "status"}
                  direction={list.direction}
                  onClick={() => list.changeSort("status")}
                />
                <SortableHeader
                  label={t(k("operations.quantity"))}
                  active={list.sort === "goodQuantity"}
                  direction={list.direction}
                  onClick={() => list.changeSort("goodQuantity")}
                />
                <th>
                  <span className="sr-only">{t(k("operations.actions"))}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.rows.map((r, i) => (
                <tr key={r.id ?? i}>
                  <td>{String(r.code ?? r.number ?? r.id).slice(0, 24)}</td>
                  <td>{String(r.name ?? r.reason ?? r.kind ?? "—")}</td>
                  <td>{String(r.status ?? "—")}</td>
                  <td>
                    {typeof (
                      r.goodQuantity ??
                      r.countedQuantity ??
                      r.invoiceAmount
                    ) === "number"
                      ? formatNumber(
                          Number(
                            r.goodQuantity ??
                              r.countedQuantity ??
                              r.invoiceAmount,
                          ),
                        )
                      : "—"}
                  </td>
                  <td>
                    <RowAction section={section} row={r} run={command} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!list.rows.length && (
          <p className="empty-state">{t(k("operations.empty"))}</p>
        )}
      </section>
    </main>
  );
}
function collection(d: OperationsSnapshot, s: OperationsSection): Row[] {
  return s === "operators"
    ? (d.operators ?? [])
    : s === "shopFloor"
      ? [...(d.laborEntries ?? []), ...(d.declarations ?? [])]
      : s === "inventoryCounts"
        ? (d.inventoryCounts ?? [])
        : s === "warehouseTransfers"
          ? (d.transfers ?? [])
          : s === "salesPickings"
            ? (d.pickings ?? [])
            : s === "salesReturns"
              ? (d.salesReturns ?? [])
              : s === "purchaseMatching"
                ? (d.purchaseMatches ?? [])
                : d.productionConfiguration
                  ? [d.productionConfiguration]
                  : [];
}
function RowAction({
  section,
  row,
  run,
}: {
  section: OperationsSection;
  row: Row;
  run: (path: string, body?: unknown) => Promise<void>;
}) {
  const { t } = useLocale();
  if (section === "shopFloor" && row.status === "Running")
    return (
      <button
        className="secondary-button"
        onClick={() =>
          run(`/labor/${row.id}/stop`, { reasonCode: "", pause: false })
        }
      >
        {t(k("operations.stop"))}
      </button>
    );
  if (section === "inventoryCounts" && row.status !== "Posted")
    return (
      <button
        className="secondary-button"
        onClick={() => run(`/inventory-counts/${row.id}/post`)}
      >
        {t(k("operations.post"))}
      </button>
    );
  if (section === "warehouseTransfers" && row.status === "Draft")
    return (
      <button
        className="secondary-button"
        onClick={() => run(`/warehouse-transfers/${row.id}/ship`)}
      >
        {t(k("operations.ship"))}
      </button>
    );
  if (section === "warehouseTransfers" && row.status === "InTransit")
    return (
      <button
        className="secondary-button"
        onClick={() => run(`/warehouse-transfers/${row.id}/receive`)}
      >
        {t(k("operations.receive"))}
      </button>
    );
  if (section === "purchaseMatching" && row.status === "Blocked")
    return (
      <button
        className="secondary-button"
        onClick={() =>
          run(`/purchase-matches/${row.id}/resolve`, {
            resolution: t(k("operations.authorizedResolution")),
          })
        }
      >
        {t(k("operations.resolve"))}
      </button>
    );
  return null;
}
function SectionForm({
  section,
  data,
  done,
}: {
  section: OperationsSection;
  data: OperationsSnapshot;
  done: () => void;
}) {
  if (section === "operators") return <OperatorForm done={done} />;
  if (section === "shopFloor") return <LaborForm data={data} done={done} />;
  if (section === "productionConfiguration")
    return <ConfigurationForm data={data} done={done} />;
  return <OperationalForm section={section} data={data} done={done} />;
}
function Shell({
  submit,
  done,
  children,
}: {
  submit: (e: FormEvent) => void;
  done: () => void;
  children: React.ReactNode;
}) {
  const { t } = useLocale();
  return (
    <AppModal
      title={t("administration.edit")}
      closeLabel={t("recordNavigation.backToList")}
      onClose={done}
      saveForm
      wide
    >
      <form className="administration-form record-form" onSubmit={submit}>
        <div className="compact-form-grid">{children}</div>
      </form>
    </AppModal>
  );
}
function OperatorForm({ done }: { done: () => void }) {
  const { t } = useLocale();
  const [f, setF] = useState({ code: "", name: "", hourlyCost: 0 });
  return (
    <Shell
      done={done}
      submit={async (e) => {
        e.preventDefault();
        await operationCommand("/operators", "POST", {
          ...f,
          userId: null,
          workCenterId: null,
          resourceId: null,
          isActive: true,
        });
        done();
      }}
    >
      <label>
        <span>{t("aplPanel.code")}</span>
        <input
          required
          value={f.code}
          onChange={(e) => setF({ ...f, code: e.target.value })}
        />
      </label>
      <label>
        <span>{t("aplPanel.name")}</span>
        <input
          required
          value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })}
        />
      </label>
      <label>
        <span>{t(k("operations.hourlyCost"))}</span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={f.hourlyCost}
          onChange={(e) => setF({ ...f, hourlyCost: Number(e.target.value) })}
        />
      </label>
    </Shell>
  );
}
function LaborForm({
  data,
  done,
}: {
  data: OperationsSnapshot;
  done: () => void;
}) {
  const { t } = useLocale();
  const [action, setAction] = useState("labor");
  const [f, setF] = useState({
    operatorId: data.operators[0]?.id ?? "",
    operationId: data.operations[0]?.id ?? "",
    orderId: data.productionOrders[0]?.id ?? "",
    kind: "Run",
    goodQuantity: 0,
  });
  return (
    <Shell
      done={done}
      submit={async (e) => {
        e.preventDefault();
        if (action === "labor")
          await operationCommand("/labor/start", "POST", {
            operatorId: f.operatorId,
            operationId: f.operationId,
            kind: f.kind,
            resourceId: null,
          });
        else
          await operationCommand(
            `/production-orders/${f.orderId}/declarations`,
            "POST",
            {
              operationId: f.operationId,
              operatorId: f.operatorId,
              goodQuantity: f.goodQuantity,
              rejectedQuantity: 0,
              reworkQuantity: 0,
              scrapReasonCode: "",
              outputWarehouseId: data.warehouses[0]?.id ?? null,
              outputLocationId: null,
              lotNumber: "",
              serialNumber: "",
              createOutput: true,
            },
          );
        done();
      }}
    >
      <label>
        <span>{t(k("operations.action"))}</span>
        <select value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="labor">{t(k("operations.startLabor"))}</option>
          <option value="declare">
            {t(k("operations.declareProduction"))}
          </option>
        </select>
      </label>
      <label>
        <span>{t(k("operations.operator"))}</span>
        <select
          required
          value={f.operatorId}
          onChange={(e) => setF({ ...f, operatorId: e.target.value })}
        >
          {data.operators.map((x) => (
            <option value={x.id} key={x.id}>
              {String(x.code)} · {String(x.name)}
            </option>
          ))}
        </select>
      </label>
      {action === "declare" && (
        <label>
          <span>{t(k("operations.order"))}</span>
          <select
            required
            value={f.orderId}
            onChange={(e) => setF({ ...f, orderId: e.target.value })}
          >
            {data.productionOrders.map((x) => (
              <option value={x.id} key={x.id}>
                {String(x.number)}
              </option>
            ))}
          </select>
        </label>
      )}
      <label>
        <span>{t(k("operations.operation"))}</span>
        <select
          required
          value={f.operationId}
          onChange={(e) => setF({ ...f, operationId: e.target.value })}
        >
          {data.operations
            .filter(
              (x) => action === "labor" || x.productionOrderId === f.orderId,
            )
            .map((x) => (
              <option value={x.id} key={x.id}>
                {String(x.code)} · {String(x.name)}
              </option>
            ))}
        </select>
      </label>
      {action === "declare" && (
        <label>
          <span>{t(k("operations.goodQuantity"))}</span>
          <input
            type="number"
            min="0.0001"
            step="0.0001"
            required
            value={f.goodQuantity}
            onChange={(e) =>
              setF({ ...f, goodQuantity: Number(e.target.value) })
            }
          />
        </label>
      )}
    </Shell>
  );
}
function ConfigurationForm({
  data,
  done,
}: {
  data: OperationsSnapshot;
  done: () => void;
}) {
  const { t } = useLocale();
  const current = data.productionConfiguration;
  const [f, setF] = useState({
    consumptionMode: String(current?.consumptionMode ?? "OperationBackflush"),
    outputWarehouseId: String(
      current?.outputWarehouseId ?? data.warehouses[0]?.id ?? "",
    ),
    outputLocationId: null,
    allowNegativeStock: Boolean(current?.allowNegativeStock),
    requireLotScan: Boolean(current?.requireLotScan),
    overproductionTolerancePercent: Number(
      current?.overproductionTolerancePercent ?? 0,
    ),
  });
  return (
    <Shell
      done={done}
      submit={async (e) => {
        e.preventDefault();
        await operationCommand("/production-configuration", "PUT", f);
        done();
      }}
    >
      <label>
        <span>{t(k("operations.consumptionMode"))}</span>
        <select
          value={f.consumptionMode}
          onChange={(e) => setF({ ...f, consumptionMode: e.target.value })}
        >
          {["Manual", "OperationBackflush", "OrderCompletion", "Mixed"].map(
            (x) => (
              <option key={x} value={x}>
                {t(k(`operations.mode.${x}`))}
              </option>
            ),
          )}
        </select>
      </label>
      <label>
        <span>{t(k("operations.outputWarehouse"))}</span>
        <select
          required
          value={f.outputWarehouseId}
          onChange={(e) => setF({ ...f, outputWarehouseId: e.target.value })}
        >
          {data.warehouses.map((x) => (
            <option value={x.id} key={x.id}>
              {String(x.code)} · {String(x.name)}
            </option>
          ))}
        </select>
      </label>
    </Shell>
  );
}
function OperationalForm({
  section,
  data,
  done,
}: {
  section: OperationsSection;
  data: OperationsSnapshot;
  done: () => void;
}) {
  const { t } = useLocale();
  const [number, setNumber] = useState("");
  const [first, setFirst] = useState("");
  const [second, setSecond] = useState("");
  const [article, setArticle] = useState("");
  const [unit, setUnit] = useState("");
  const [line, setLine] = useState("");
  const [quantity, setQuantity] = useState(1);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (section === "inventoryCounts")
      await operationCommand("/inventory-counts", "POST", {
        number,
        warehouseId: first,
        lines: [
          {
            articleId: article,
            unitId: unit,
            locationId: null,
            lotNumber: "",
            serialNumber: "",
            countedQuantity: quantity,
          },
        ],
      });
    if (section === "warehouseTransfers")
      await operationCommand("/warehouse-transfers", "POST", {
        number,
        sourceWarehouseId: first,
        targetWarehouseId: second,
        lines: [{ articleId: article, unitId: unit, quantity }],
      });
    if (section === "salesPickings")
      await operationCommand("/sales-pickings", "POST", {
        number,
        salesOrderId: first,
        warehouseId: second,
      });
    if (section === "salesReturns")
      await operationCommand("/sales-returns", "POST", {
        number,
        customerId: first,
        salesDeliveryId: second,
        reason: "",
        lines: [
          {
            deliveryLineId: line,
            quantity,
            resolution: "Quarantine",
            lotNumber: "",
            serialNumber: "",
          },
        ],
      });
    if (section === "purchaseMatching")
      await operationCommand("/purchase-matches", "POST", {
        purchaseInvoiceId: first,
        toleranceAmount: quantity,
      });
    done();
  };
  const firstRefs =
    section === "salesPickings"
      ? data.salesOrders
      : section === "purchaseMatching"
        ? data.purchaseInvoices
        : section === "inventoryCounts" || section === "warehouseTransfers"
          ? data.warehouses
          : [];
  return (
    <Shell done={done} submit={submit}>
      {section !== "purchaseMatching" && (
        <label>
          <span>{t(k("operations.number"))}</span>
          <input
            required
            value={number}
            onChange={(e) => setNumber(e.target.value)}
          />
        </label>
      )}
      <label>
        <span>{t(k("operations.reference"))}</span>
        {section === "salesReturns" ? (
          <input
            required
            value={first}
            onChange={(e) => setFirst(e.target.value)}
            aria-label={t(k("operations.customerId"))}
          />
        ) : (
          <select
            required
            value={first}
            onChange={(e) => setFirst(e.target.value)}
          >
            <option value="">—</option>
            {firstRefs.map((x) => (
              <option key={x.id} value={x.id}>
                {String(x.number ?? x.code ?? x.id)}
              </option>
            ))}
          </select>
        )}
      </label>
      {section === "warehouseTransfers" || section === "salesPickings" ? (
        <label>
          <span>{t(k("operations.destination"))}</span>
          <select
            required
            value={second}
            onChange={(e) => setSecond(e.target.value)}
          >
            <option value="">—</option>
            {data.warehouses.map((x) => (
              <option key={x.id} value={x.id}>
                {String(x.code ?? x.id)}
              </option>
            ))}
          </select>
        </label>
      ) : section === "salesReturns" ? (
        <>
          <label>
            <span>{t(k("operations.deliveryId"))}</span>
            <input
              required
              value={second}
              onChange={(e) => setSecond(e.target.value)}
            />
          </label>
          <label>
            <span>{t(k("operations.deliveryLineId"))}</span>
            <input
              required
              value={line}
              onChange={(e) => setLine(e.target.value)}
            />
          </label>
        </>
      ) : null}
      {section === "inventoryCounts" || section === "warehouseTransfers" ? (
        <>
          <label>
            <span>{t(k("operations.articleId"))}</span>
            <input
              required
              value={article}
              onChange={(e) => setArticle(e.target.value)}
            />
          </label>
          <label>
            <span>{t(k("operations.unitId"))}</span>
            <input
              required
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
            />
          </label>
        </>
      ) : null}
      <label>
        <span>
          {t(
            k(
              section === "purchaseMatching"
                ? "operations.tolerance"
                : "operations.quantity",
            ),
          )}
        </span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={quantity}
          onChange={(e) => setQuantity(Number(e.target.value))}
        />
      </label>
    </Shell>
  );
}
